import { ChangeDetectorRef, DestroyRef, inject, signal } from '@angular/core';
import { DynamicFieldsetModule, FsOnAfterSaveEvent, ModelFormServiceFactory } from '@common/forms/frontend-shared';
import { UpdatePasswordRequest } from '@common/auth-strategy-credentials/shared';
import { AccessFacade, AccessState, ConfigService, CoreMessagesModule, LocalMessageHandlerService } from '@core/frontend-shared';
import { Store } from '@ngxs/store';
import { takeWhileAlive } from '@core/frontend-shared/utils';
import { filter, map } from 'rxjs/operators';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { AppMessagesComponent } from '@common/messages/frontend-shared';
import { UiButtonComponent } from '@common/ui/frontend-shared/button';
import { UiIconsModule } from '@common/ui/frontend-shared/icons';
import { UiFieldWrapperComponent } from '@common/ui/frontend-shared/field-wrapper';
import { UiInputTextComponent } from '@common/ui/frontend-shared/input-text';
import { SESSION_REQUIRE_NEW_PASSWORD } from '@core/shared/access';
import * as i0 from "@angular/core";
import * as i1 from "@core/frontend-shared";
import * as i2 from "@common/forms/frontend-shared";
import * as i3 from "../../../../../forms/frontend-shared/src/lib/form-submit-button/form-submit-button.directive";
import * as i4 from "@angular/forms";
const _c0 = () => ({
  label: "Speichern"
});
function ForceNewPasswordComponent_Conditional_0_Conditional_9_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "form", 5)(1, "ui-field-wrapper", 6);
    i0.ɵɵelement(2, "ui-input-text", 7);
    i0.ɵɵelementEnd();
    i0.ɵɵelementStart(3, "ui-field-wrapper", 6);
    i0.ɵɵelement(4, "ui-input-text", 8);
    i0.ɵɵelementEnd();
    i0.ɵɵelementStart(5, "div", 9);
    i0.ɵɵelement(6, "app-messages")(7, "button", 10);
    i0.ɵɵelementEnd()();
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext(2);
    i0.ɵɵproperty("formGroup", ctx_r0.formService.formGroup);
    i0.ɵɵadvance();
    i0.ɵɵproperty("placeholder", "Passwort");
    i0.ɵɵadvance(2);
    i0.ɵɵproperty("placeholder", "Passwort Wiederh.");
    i0.ɵɵadvance(4);
    i0.ɵɵproperty("formSubmitButton", ctx_r0.formService)("formSubmitButtonOptions", i0.ɵɵpureFunction0(5, _c0));
  }
}
function ForceNewPasswordComponent_Conditional_0_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "div", 2)(1, "div", 3, 0)(3, "div", 4)(4, "h2");
    i0.ɵɵtext(5, "Neues Passwort vergeben");
    i0.ɵɵelementEnd();
    i0.ɵɵelementStart(6, "p");
    i0.ɵɵtext(7, "Bitte geben Sie Ihr gew\u00FCnschtes Passwort ein.");
    i0.ɵɵelementEnd();
    i0.ɵɵelement(8, "br");
    i0.ɵɵconditionalCreate(9, ForceNewPasswordComponent_Conditional_0_Conditional_9_Template, 8, 6, "form", 5);
    i0.ɵɵelementEnd()()();
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵclassMap("position-overlay");
    i0.ɵɵadvance(9);
    i0.ɵɵconditional(ctx_r0.formService.initialized ? 9 : -1);
  }
}
/**
 * Important: We cannot rely on Observables in here.
 * When login status changes, this component will be recreated.
 * This will create a code loop.
 * So we need to do a redirect to another route to break the loop.
 * TODO: #86c2vj799 #86c2vj799 - component should be in root app and auto-created
 */
export let ForceNewPasswordComponent = /*#__PURE__*/(() => {
  class ForceNewPasswordComponent {
    constructor(cd, access, messages, config, formServiceFactory) {
      this.cd = cd;
      this.access = access;
      this.messages = messages;
      this.config = config;
      this.formServiceFactory = formServiceFactory;
      this.destroyRef = inject(DestroyRef);
      this.sessionData$ = inject(Store).select(AccessState.sessionData);
      this.display = signal(false, ...(ngDevMode ? [{
        debugName: "display"
      }] : /* istanbul ignore next */[]));
    }
    ngOnInit() {
      this.sessionData$.pipe(map(sessionData => {
        return sessionData[SESSION_REQUIRE_NEW_PASSWORD] || false;
      }), takeWhileAlive(this.destroyRef)).subscribe(requireNewPassword => {
        if (requireNewPassword) {
          this.prepareDisplay();
        }
        this.display.set(requireNewPassword);
      });
    }
    prepareDisplay() {
      if (this.formService) return;
      this.formService = this.formServiceFactory.useDestroyRef(this.destroyRef).createModelForm(UpdatePasswordRequest);
      this.formService.setImmutableValues({
        forcedUpdate: true
      });
      this.formService.events$.pipe(takeWhileAlive(this.destroyRef), filter(e => e instanceof FsOnAfterSaveEvent)).subscribe(e => {
        if (e.payload.error) {
          this.messages.push(e.payload.error);
        } else {
          this.display.set(false);
          this.access.refreshSession();
          this.cd.markForCheck();
        }
      });
      this.formService.initialize();
    }
    static {
      this.ɵfac = function ForceNewPasswordComponent_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || ForceNewPasswordComponent)(i0.ɵɵdirectiveInject(i0.ChangeDetectorRef), i0.ɵɵdirectiveInject(i1.AccessFacade), i0.ɵɵdirectiveInject(i1.LocalMessageHandlerService), i0.ɵɵdirectiveInject(i1.ConfigService), i0.ɵɵdirectiveInject(i2.ModelFormServiceFactory));
      };
    }
    static {
      this.ɵcmp = /*@__PURE__*/i0.ɵɵdefineComponent({
        type: ForceNewPasswordComponent,
        selectors: [["auth-strategy-credentials-force-new-password"]],
        features: [i0.ɵɵProvidersFeature([LocalMessageHandlerService])],
        decls: 1,
        vars: 1,
        consts: [["dialog", ""], [1, "wrapper", 3, "class"], [1, "wrapper"], [1, "dialog"], [1, "content"], [3, "formGroup"], [3, "placeholder"], ["type", "password", "formControlName", "password", 1, "ui-width-stretch"], ["type", "password", "formControlName", "password2", 1, "ui-width-stretch"], [1, "ui-margin"], ["uiButton", "", "icon", "unlock", 1, "ui-width-stretch", 3, "formSubmitButton", "formSubmitButtonOptions"]],
        template: function ForceNewPasswordComponent_Template(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵconditionalCreate(0, ForceNewPasswordComponent_Conditional_0_Template, 10, 3, "div", 1);
          }
          if (rf & 2) {
            i0.ɵɵconditional(ctx.display() ? 0 : -1);
          }
        },
        dependencies: [DynamicFieldsetModule, i3.FormSubmitButtonDirective, FormsModule, i4.ɵNgNoValidate, i4.NgControlStatus, i4.NgControlStatusGroup, ReactiveFormsModule, i4.FormGroupDirective, i4.FormControlName, CoreMessagesModule, AppMessagesComponent, UiIconsModule, UiButtonComponent, UiFieldWrapperComponent, UiInputTextComponent],
        styles: [".wrapper[_ngcontent-%COMP%]{position:fixed;z-index:1000}.dialog[_ngcontent-%COMP%]{background:var(--contentColor);color:var(--contentTextColor);padding:8px 12px 12px;border-radius:4px}.dialog[_ngcontent-%COMP%]   .content[_ngcontent-%COMP%]{padding:0 0 10px}.wrapper.position-overlay[_ngcontent-%COMP%]{background:var(--invertedOverlaySoftColor);-webkit-backdrop-filter:blur(4px);backdrop-filter:blur(4px);inset:0}.wrapper.position-overlay[_ngcontent-%COMP%]{display:flex;align-items:center;justify-content:center}.wrapper.position-overlay[_ngcontent-%COMP%]   .dialog[_ngcontent-%COMP%]{position:static}@media (min-width: 768px){.dialog[_ngcontent-%COMP%]{padding:20px 20px 22px}.position-overlay[_ngcontent-%COMP%]   .dialog[_ngcontent-%COMP%], .position-modal[_ngcontent-%COMP%]   .dialog[_ngcontent-%COMP%]{bottom:var(--trackingConsentPositionBottom, 20px);left:initial;left:20px;width:600px;min-width:400px;max-width:40%}}"]
      });
    }
  }
  return ForceNewPasswordComponent;
})();